import React, { useMemo, useState, useEffect } from 'react';
import axios from 'axios';
import DataTable from 'react-data-table-component';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye } from '@fortawesome/free-solid-svg-icons';
import CustomSelect from './widgets/CustomSelect';
import EmployeeSalaryStatement from './EmployeeSalaryStatement';
import { useAuth } from '../hooks/useAuth';
import '../styles/CustomSelect.css';
import '../styles/EmployeeSalaries.css';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const buildPayPeriods = (startYear = 2024) => {
  const endYear = new Date().getFullYear() + 1;
  const periods = [];
  for (let year = endYear; year >= startYear; year -= 1) {
    for (let monthIndex = MONTH_NAMES.length - 1; monthIndex >= 0; monthIndex -= 1) {
      periods.push(`${year} ${MONTH_NAMES[monthIndex]}`);
    }
  }
  return periods;
};

const EmployeeSalaries = () => {
  const { hasPermission } = useAuth();
  const canViewPay = hasPermission('/employee-list/pay', 'View');
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [payPeriod, setPayPeriod] = useState('');
  const [branch, setBranch] = useState('');
  const [paymentsData, setPaymentsData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalPayment, setTotalPayment] = useState(0);
  const [employeeCount, setEmployeeCount] = useState(0);
  const [selectedSummary, setSelectedSummary] = useState(null);

  const payPeriods = useMemo(() => buildPayPeriods(), []);
  const branchOptions = ['Kissy', 'Poultney'];
  const employeeOptions = useMemo(
    () =>
      employees.map((employee) => ({
        value: String(employee.employee_id),
        label: employee.employee_name,
      })),
    [employees]
  );

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/employees/`);
        setEmployees(response.data);
      } catch (error) {
        console.error('Error fetching employees:', error);
      }
    };
    fetchEmployees();
  }, []);

  const handleSearch = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/employees/salaries`, {
        params: {
          employee_id: selectedEmployee || undefined,
          pay_period: payPeriod || undefined,
          branch: branch || undefined,
        },
      });
      setPaymentsData(response.data);

      const total = canViewPay
        ? response.data.reduce((sum, record) => sum + parseFloat(record.total_salary), 0)
        : null;
      setTotalPayment(total);

      const uniqueEmployees = new Set(response.data.map((record) => record.employee_name));
      setEmployeeCount(uniqueEmployees.size);
    } catch (error) {
      console.error('Error fetching employee payments:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const openStatement = (row) => {
    if (!row) return;
    if (row.employee_id) {
      setSelectedSummary(row);
      return;
    }
    const match = employees.find(
      (employee) =>
        employee.employee_name === row.employee_name &&
        (!row.branch || employee.branch === row.branch)
    );
    setSelectedSummary({ ...row, employee_id: match?.employee_id });
  };

  const columns = [
    { name: 'Employee', selector: (row) => row.employee_name, sortable: true },
    { name: 'Pay Period', selector: (row) => row.pay_period, sortable: true },
    { name: 'Contracted Salary', selector: (row) => (canViewPay ? row.salary : '—'), sortable: true },
    { name: 'Branch', selector: (row) => row.branch, sortable: true },
    { name: '# of Pmts', selector: (row) => row.number_of_payments, sortable: true },
    { name: 'Total Salary', selector: (row) => (canViewPay ? row.total_salary : '—'), sortable: true, right: true },
    {
      name: 'View',
      button: true,
      width: '72px',
      ignoreRowClick: true,
      cell: (row) => (
        <button
          type="button"
          className="salary-view-button"
          title="View pay statement"
          aria-label="View pay statement"
          onClick={() => openStatement(row)}
        >
          <FontAwesomeIcon icon={faEye} />
        </button>
      ),
    },
  ];

  const conditionalRowStyles = [
    {
      when: (row) => {
        if (!canViewPay) return false;
        const leaveAllowance = parseFloat(row.leave_allowance) || 0;
        return parseFloat(row.total_salary) - leaveAllowance > parseFloat(row.salary) * 1.03;
      },
      style: {
        backgroundColor: 'rgba(255, 0, 0, 0.1)',
        color: '#000',
      },
    },
  ];

  return (
    <div className="employee-salaries-container">
      <h2>Employee Salaries</h2>
      <div className="filter-form">
        <div className="form-group">
          <label>Employee:</label>
          <CustomSelect
            name="employee_id"
            value={selectedEmployee}
            onChange={(_, value) => setSelectedEmployee(value || '')}
            options={employeeOptions}
            isSearchable
            isClearable
            placeholder="All employees"
          />
        </div>

        <div className="form-group">
          <label htmlFor="employee-salaries-period">Pay Period:</label>
          <select
            id="employee-salaries-period"
            value={payPeriod}
            onChange={(e) => setPayPeriod(e.target.value)}
          >
            <option value="">All Pay Periods</option>
            {payPeriods.map((period) => (
              <option key={period} value={period}>
                {period}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="employee-salaries-branch">Branch:</label>
          <select
            id="employee-salaries-branch"
            value={branch}
            onChange={(e) => setBranch(e.target.value)}
          >
            <option value="">All Branches</option>
            {branchOptions.map((branchOption) => (
              <option key={branchOption} value={branchOption}>
                {branchOption}
              </option>
            ))}
          </select>
        </div>

        <div className="form-actions">
          <button type="button" onClick={handleSearch} disabled={isLoading}>
            {isLoading ? 'Searching...' : 'Search'}
          </button>
        </div>
      </div>

      <div className="data-table-container">
        <div className="summary-row">
          <strong>
            Total Payment: {canViewPay
              ? Number(totalPayment || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })
              : '—'}
          </strong>
          {' | '}
          <strong>Employee Count: {employeeCount}</strong>
        </div>
        <p className="salary-row-hint">Click a row to view the pay statement for that employee and period.</p>

        <DataTable
          columns={columns}
          data={paymentsData}
          pagination
          paginationPerPage={30}
          highlightOnHover
          pointerOnHover
          progressPending={isLoading}
          conditionalRowStyles={conditionalRowStyles}
          onRowClicked={openStatement}
        />
      </div>

      <button type="button" className="export-button">Export to Excel</button>

      {selectedSummary && (
        <EmployeeSalaryStatement
          summary={selectedSummary}
          canViewPay={canViewPay}
          onClose={() => setSelectedSummary(null)}
        />
      )}
    </div>
  );
};

export default EmployeeSalaries;
